import { useCompatRouter as useRouter } from "@/lib/compat-router/context";

const FILTER_LABEL_FORMATTERS: Record<string, (val: string) => string> = {
  listingType: (val) => `For ${val.replace(/-/g, " ").toUpperCase()}`,
  location: (val) => `Location: ${val}`,
  propertyType: (val) => `Type: ${val}`,
  propertyCategory: (val) =>
    `Category: ${val
      .toLowerCase()
      .split("_")
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(" ")}`,
  bedroomCount: (val) => `Bedrooms: ${val}`,
  bathroomCount: (val) => `Bathrooms: ${val}`,
  minPrice: (val) => {
    const amount = parseInt(val, 10);
    if (isNaN(amount)) return `Min Price: ${val}`;
    return `Min: ₦${amount >= 1000000 ? `${amount / 1000000}M` : `${amount / 1000}K`}`;
  },
  maxPrice: (val) => {
    const amount = parseInt(val, 10);
    if (isNaN(amount)) return `Max Price: ${val}`;
    return `Max: ₦${amount >= 1000000 ? `${amount / 1000000}M` : `${amount / 1000}K`}`;
  },
  furnished: (val) => (val === "true" ? "Furnished" : "Unfurnished"),
  serviced: (val) => (val === "true" ? "Serviced" : "Unserviced"),
  shared: (val) => (val === "true" ? "Shared Space" : "Private Space"),
  addedToSite: (val) => `Added: ${val.replace(/([A-Z])/g, " $1").trim()}`,
};

interface PropertyResultsHeaderProps {
  onApply?: (params: Record<string, string>) => void;
}

const PropertyResultsHeader = ({ onApply }: PropertyResultsHeaderProps) => {
  const router = useRouter();

  // 1. Isolate active filter parameters directly from the native router instance
  const activeFilters = Object.entries(router.query).filter(
    ([key, value]) =>
      value &&
      key !== "page" &&
      key !== "pageSize" &&
      key !== "realtorId" &&
      value !== "Any" &&
      value !== "",
  );

  const hasFilters = activeFilters.length > 0;

  // 2. Clear all filters redirecting to clean view state
  const handleClearAll = () => {
    const { realtorId, ...rest } = router.query;
    const clearedQuery = realtorId ? { realtorId } : {};

    const params: Record<string, string> = {};
    if (realtorId) {
      params.realtorId = Array.isArray(realtorId)
        ? realtorId[0]
        : realtorId.toString();
    }

    router.push(
      {
        pathname: router.pathname,
        query: clearedQuery,
      },
      undefined,
      { shallow: true },
    );

    onApply?.(params);
  };

  // 3. Remove single parameter directly by reading fresh state from router.query
  const handleRemoveFilter = (keyToRemove: string) => {
    const updatedQuery = { ...router.query };

    delete updatedQuery[keyToRemove];
    delete updatedQuery.page; // Reset pagination automatically on filter change

    const params: Record<string, string> = {};
    Object.entries(updatedQuery).forEach(([key, value]) => {
      if (!value || key === "page" || key === "pageSize") return;
      if (key === "realtorId") {
        params.realtorId = Array.isArray(value) ? value[0] : value.toString();
        return;
      }
      if (value !== "Any" && value !== "") {
        params[key] = Array.isArray(value) ? value[0] : value.toString();
      }
    });

    router.replace(
      {
        pathname: router.pathname,
        query: updatedQuery,
      },
      undefined,
      { shallow: true },
    );

    onApply?.(params);
  };

  if (!hasFilters) return null;

  return (
    <div className="w-full max-w-6xl mx-auto px-4 py-5 border-b border-[#163e4c]/20 mb-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        {/* Left Side: Summary text & Pills wrapper */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 flex-wrap">
          <span className="text-sm font-semibold text-gray-400">
            Showing results for:
          </span>

          {/* Active filter pills */}
          <div className="flex flex-wrap gap-2">
            {activeFilters.map(([key, value]) => {
              const displayValue = value ? value.toString() : "";
              const formatter = FILTER_LABEL_FORMATTERS[key];
              const displayLabel = formatter
                ? formatter(displayValue)
                : `${displayValue}`;

              return (
                <div
                  key={key}
                  className="flex items-center gap-1.5 bg-[#0c2e3a]/5 hover:bg-[#0c2e3a]/10 text-[#0c2e3a] dark:bg-white/5 dark:text-white/90 border border-[#0c2e3a]/10 dark:border-white/10 px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-150"
                >
                  <span>
                    {displayLabel == "For SALE" ? "Buy" : displayLabel}
                  </span>
                  <button
                    type="button"
                    onClick={() => handleRemoveFilter(key)}
                    className="hover:bg-red-500 hover:text-white dark:hover:bg-red-500/20 dark:hover:text-red-400 rounded-full p-0.5 transition-colors focus:outline-none"
                    aria-label={`Remove filter ${key}`}
                  >
                    <svg
                      className="h-3 w-3"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2.5"
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>
              );
            })}
          </div>
        </div>

        {/* Right Side: Clear Trigger */}
        <button
          type="button"
          onClick={handleClearAll}
          className="self-start cursor-pointer md:self-auto text-xs font-bold text-red-500 hover:text-red-600 dark:text-red-400 dark:hover:text-red-300 transition-colors uppercase tracking-wider flex items-center gap-1.5 py-1 px-2 hover:bg-red-500/5 rounded-lg focus:outline-none"
        >
          <span>Clear all filters</span>
          <svg
            className="w-3.5 h-3.5"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
            />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default PropertyResultsHeader;
